import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router';

import groupBy from 'lodash/groupBy';
import isNaN from 'lodash/isNaN';

import config from 'js/app/config';
import { languageCodeToName } from 'js/lib/language';
import { simplifyQueryValue } from 'js/lib/queryUtils';
import { useLocation } from 'js/lib/useRouter';

import { itemsPerPage, root } from 'bundles/directory-graphql/common/constants';
import { getQueryLinksPerPage } from 'bundles/directory-graphql/common/utils';
import LinksContainer from 'bundles/directory-graphql/components/LinksContainer';
import type { TitledLink } from 'bundles/directory-graphql/components/LinksContainer';
import Metatags from 'bundles/directory-graphql/components/MetatagsWithPagination';
import NotFound from 'bundles/directory-graphql/components/NotFound';
import useGetAllEqpData from 'bundles/directory-graphql/hooks/useGetAllEqpData';
import PaginationControls from 'bundles/page/components/cds/PaginationControls';
import { useIntl } from 'bundles/page/lib/useIntl';

import _t from 'i18n!nls/directory-graphql';

export const Pagination = ({ total, pageNumber }: { total: number; pageNumber: number }) => {
  const pageCount = Math.ceil(total / itemsPerPage);
  return (
    <PaginationControls currentPage={pageNumber} largeStyle pageCount={pageCount} maxPages={4} renderLinks={true} />
  );
};
export const QueriesMetatags = ({ total }: { total: number }) => {
  const additionalSettings = {
    title: _t('Queries Directory'),
    description: _t('The Coursera Queries directory includes links to some of our popular catalog search pages.'),
    disableCrawlerIndexing: true,
    useFollowForDisablingIndexing: true,
  };

  return <Metatags total={total} additionalSettings={additionalSettings} />;
};

const PaginatedLinkView = ({ titledLinks }: { titledLinks: TitledLink[] }) => {
  const { query } = useLocation();
  const { page } = query;

  const total = titledLinks.length || 0;
  const pageNumber = parseInt(simplifyQueryValue(page) ?? '1', 10);
  const pageCount = Math.ceil(total / itemsPerPage);

  if (isNaN(pageNumber) || pageNumber < 1 || pageNumber > pageCount) {
    return <NotFound />;
  }

  return (
    <div>
      <LinksContainer titledLinks={getQueryLinksPerPage(titledLinks, simplifyQueryValue(page))} />
      <Pagination total={total} pageNumber={pageNumber} />
      <QueriesMetatags total={total} />
    </div>
  );
};

export const Queries = () => {
  const intl = useIntl();
  const { pathname, query } = useLocation();
  // Since query params aren't typed, this will have to do in the meantime
  const localeCodeFromQuery: string | undefined = simplifyQueryValue(query.localeCode);
  const { allEqpData } = useGetAllEqpData();

  const dataGroupedByLocale = groupBy(allEqpData, ({ localeCode }) => localeCode || 'en-US');

  if (
    allEqpData.length < 1 ||
    // We need to cover the case where an invalid locale code is passed through
    (localeCodeFromQuery && !Object.keys(dataGroupedByLocale).includes(localeCodeFromQuery))
  ) {
    return <NotFound />;
  }

  const locales = Object.entries(dataGroupedByLocale).map(([localeCode, items]) => ({
    localeCode,
    count: items.length,
  }));

  const topLevelLinks = locales.map(({ localeCode, count }) => ({
    title: intl.formatMessage(_t(`{count} Popular {count, plural, =1 {Topic} other {Topics}} in {language}`), {
      count,
      language: languageCodeToName(localeCode),
    }),
    link: new URL(`${pathname}?localeCode=${localeCode}`, config.url.base).toString(),
  }));

  return (
    <div>
      {!localeCodeFromQuery ? (
        <PaginatedLinkView titledLinks={topLevelLinks} />
      ) : (
        <React.Fragment>
          <FormattedMessage
            message={_t(`Popular topics in {locale}. Click {linkBack} to go back.`)}
            locale={languageCodeToName(localeCodeFromQuery)}
            linkBack={<Link href={`${root}/queries`}>{_t('here')}</Link>}
          />
          {/*
           // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess */}
          <PaginatedLinkView titledLinks={dataGroupedByLocale[localeCodeFromQuery]} />
        </React.Fragment>
      )}
    </div>
  );
};

export default Queries;
